import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { entranceCards, heroStats, siteMeta } from '@/mocks/homeData';
import EntranceCard, { type EntranceCardData } from './EntranceCard';

export default function HeroSection() {
  const { copiedId, copy } = useCopyToClipboard();
  const isCopied = copiedId === 'hero-primary';

  const cards: EntranceCardData[] = entranceCards.map((card, index) => ({ ...card, index }));

  return (
    <section id="top" className="relative w-full pt-28 md:pt-36 pb-12 md:pb-16 overflow-hidden">
      <div className="absolute inset-0 grain-paper opacity-60 pointer-events-none" aria-hidden="true" />
      <div
        className="absolute -top-24 right-0 w-[520px] h-[520px] rounded-full bg-primary-100/50 blur-3xl pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="absolute top-40 -left-24 w-[420px] h-[420px] rounded-full bg-accent-100/50 blur-3xl pointer-events-none"
        aria-hidden="true"
      />

      <div className="relative w-full px-4 md:px-10">
        {/* 上疏：品牌与主张，留白充足 */}
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-100 border border-background-300 text-xs font-label text-foreground-700 animate-fade-in">
            <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse-dot" aria-hidden="true" />
            {siteMeta.updatedLabel} {siteMeta.updatedAt} · 全部线路已校验
          </span>

          <h1 className="mt-6 font-heading text-3xl md:text-5xl lg:text-[56px] font-bold leading-[1.15] tracking-tight text-foreground-950 animate-fade-up">
            黑料地址最新入口，
            <br className="hidden sm:block" />
            收藏一次就<span className="text-primary-600">不再走丢</span>
          </h1>

          <p className="mt-6 text-base md:text-lg leading-relaxed text-foreground-700 animate-fade-up" style={{ animationDelay: '120ms' }}>
            {siteMeta.description}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 animate-fade-up" style={{ animationDelay: '220ms' }}>
            <button
              type="button"
              onClick={() => copy(siteMeta.primaryUrl, 'hero-primary')}
              className={`inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md text-sm font-label whitespace-nowrap cursor-pointer transition-colors duration-200 ${
                isCopied ? 'bg-accent-500 text-background-50' : 'bg-primary-500 text-background-50 hover:bg-primary-600'
              }`}
            >
              <i className={isCopied ? 'ri-check-line' : 'ri-download-2-line'} aria-hidden="true" />
              {isCopied ? '最新入口已复制' : '复制最新入口'}
            </button>
            <a
              href="#guide"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md border border-background-300 bg-background-50 text-sm font-label text-foreground-800 hover:border-primary-300 hover:text-primary-700 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-guide-line" aria-hidden="true" />
              先看使用指南
            </a>
          </div>
        </div>

        {/* 上疏：数据条 */}
        <div className="mt-10 md:mt-12 grid grid-cols-2 lg:grid-cols-4 gap-px bg-background-200 rounded-lg overflow-hidden border border-background-200 animate-fade-up" style={{ animationDelay: '300ms' }}>
          {heroStats.map((item) => (
            <div key={item.id} className="bg-background-50 px-5 py-5">
              <p className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                {item.value}
                <span className="ml-1 text-sm font-label font-normal text-foreground-500">{item.unit}</span>
              </p>
              <p className="mt-1.5 text-xs md:text-[13px] font-label text-foreground-600">{item.label}</p>
            </div>
          ))}
        </div>

        {/* 首屏主导：多卡并列 */}
        <div id="entrances" className="scroll-mt-24 mt-12 md:mt-14">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">当前可用入口一览</h2>
              <p className="mt-1.5 text-sm font-label text-foreground-600">
                三条线路并列展示，任选一条复制即可打开
              </p>
            </div>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-900 text-xs font-label whitespace-nowrap">
              <i className="ri-shield-check-line" aria-hidden="true" />
              {siteMeta.issueNote}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {cards.map((card) => (
              <EntranceCard key={card.id} data={card} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}